import { Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { AddressInputBottomSheet } from "@src/appV2/Address/components/AddressInputBottomSheet";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { getCurrentLocationStatus } from "@src/appV2/Location/utils";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";

import { OnboardingTitle } from "../../../SharedTitle";
import { type OnboardingStepProps } from "../../OnboardingStepWrapper";
import { useAddressStep } from "../hooks/useAddressStep";

export function AddressStep(props: OnboardingStepProps) {
  const { onNext } = props;

  const { data: worker } = useGetWorker({ enabled: false });

  const {
    handleUseCurrentLocation,
    loadingData,
    place,
    addressInputModalState,
    isUpdatingOnboardingAddress,
    handleSave,
    isLoadingWorker,
    handlePlaceChange,
    handleAddressInputChange,
    addressInput,
    handleManualEntry,
    addressInputErrorMessage,
    isEnablingGeoLocation,
  } = useAddressStep({
    onNext,
  });

  if (loadingData) {
    return null;
  }

  return (
    <>
      <PageContent>
        <Stack spacing={4}>
          <OnboardingTitle text="Home Location" />
          <Text semibold variant="body1">
            Enter your home address to see nearby work opportunities
          </Text>
        </Stack>
      </PageContent>

      <PageFooterWrapper>
        <Stack spacing={3}>
          <LoadingButton
            variant="brand"
            size="large"
            startIconType="map-pin"
            isLoading={isEnablingGeoLocation}
            onClick={async () => {
              logEvent(APP_V2_USER_EVENTS.ONBOARDING_HOME_ADDRESS_BUTTON_TAPPED, {
                workerId: worker?.userId,
                buttonTapped: "CURRENT_LOCATION",
              });
              await handleUseCurrentLocation();

              try {
                const { locationServicesPermission } = await getCurrentLocationStatus();
                logEvent(APP_V2_USER_EVENTS.ONBOARDING_HOME_ADDRESS_LOCATION_PERMISSION_RESULT, {
                  workerId: worker?.userId,
                  permissionGiven: locationServicesPermission,
                });
              } catch (error) {
                logError(
                  APP_V2_USER_EVENTS.ONBOARDING_HOME_ADDRESS_LOCATION_PERMISSION_RESULT_ERROR,
                  {
                    error,
                    metadata: {
                      workerId: worker?.userId,
                    },
                  }
                );
              }
            }}
          >
            Use current location
          </LoadingButton>
          <Button
            variant="outlined"
            size="large"
            disabled={isEnablingGeoLocation}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.ONBOARDING_HOME_ADDRESS_BUTTON_TAPPED, {
                workerId: worker?.userId,
                buttonTapped: "MANUAL",
              });
              handleManualEntry();
            }}
          >
            Enter manually
          </Button>
        </Stack>
      </PageFooterWrapper>

      <AddressInputBottomSheet
        modalState={addressInputModalState}
        place={place}
        isContinueLoading={isUpdatingOnboardingAddress}
        isContinueDisabled={
          isLoadingWorker || !place.address?.formatted || Boolean(addressInputErrorMessage)
        }
        addressInput={addressInput}
        addressInputErrorMessage={addressInputErrorMessage}
        onPlaceChanged={(newPlace) => {
          handlePlaceChange(newPlace);
        }}
        onContinue={handleSave}
        onAddressInputChange={handleAddressInputChange}
      />
    </>
  );
}
